// src/contexts/AuthContext.js - Updated with custom token support
import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  signOut 
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Local dev: skip Firebase and immediately set a mock authenticated user
    if (process.env.REACT_APP_ENVIRONMENT === 'local') {
      setUser({
        uid: 'local-dev-uid-1',
        email: 'dev@localhost',
        displayName: 'Local Dev User',
        emailVerified: true,
        getIdToken: async () => 'local-dev-token-not-verified',
      });
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    if (process.env.REACT_APP_ENVIRONMENT === 'local') {
      return { success: true, user };
    }
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Keep signup for backward compatibility, but it won't be used in the new flow
  const signup = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // New method for custom token authentication (used in user activation)
  const signInWithCustomTokenMethod = async (customToken) => {
    try {
      const result = await signInWithCustomToken(auth, customToken);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    if (process.env.REACT_APP_ENVIRONMENT === 'local') {
      return { success: true };
    }
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    login,
    signup,
    signInWithCustomTokenMethod,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
// PropTypes
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};